import {
  FeatureFlagsContext,
  GraphDataQualityWarning,
  GraphQLErrorCode,
  reportGraphQLError,
  SiteUrlsContext,
  WarningStatusChip,
  WarningStatusProps,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Query } from 'react-apollo';
import {
  DataDetailsViewUpstreamDataQualityWarningsQuery,
  DataDetailsViewUpstreamDataQualityWarningsQueryVariables
} from '../../../gen-src/queries/DataDetailsViewUpstreamDataQualityWarningsQuery';
import {
  DataDetailsViewUpstreamDataQualityWarningsQueryGql
} from '../../queries/DataDetailsViewUpstreamDataQualityWarningsQueryGql';
import { renderQueryResult } from '../renderQueryResult';

export type ViewHeaderDataQualityWarningProps = {
  readonly datasourceIds: string[];
};

type WarningQuery = DataDetailsViewUpstreamDataQualityWarningsQuery;
type WarningVariables = DataDetailsViewUpstreamDataQualityWarningsQueryVariables;

export function DataQualityWarningViewHeader(props: ViewHeaderDataQualityWarningProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);
  const urls = React.useContext(SiteUrlsContext);
  const renderers = React.useMemo(() => {
    return {
      renderData: renderWarnings(urls),
      renderDataWithError: renderWarningsWithError(urls)
    };
  }, [urls]);

  return (
    <Query<WarningQuery, WarningVariables>
      query={DataDetailsViewUpstreamDataQualityWarningsQueryGql}
      variables={{ datasourceIds: props.datasourceIds, first: 10, offset: 0, publishedConnectionsLineageEnabled }}
    >
      {result => renderQueryResult(result, renderers)}
    </Query>
  );
}

function renderWarningsWithError(urls: SiteUrls): (data: WarningQuery, errorCode: GraphQLErrorCode | null) => React.ReactNode {
  return (data: WarningQuery, errorCode: GraphQLErrorCode | null): React.ReactNode => {
    // Ignore the PERMISSIONS_MODE_SWITCHED error, which should not block Data Details from being shown
    if (errorCode === null || errorCode === 'PERMISSIONS_MODE_SWITCHED') {
      return renderWarnings(urls)(data);
    } else {
      return reportGraphQLError(errorCode); // Behave as renderQueryResult would have
    }
  };
}

function renderWarnings(urls: SiteUrls): (data: WarningQuery) => JSX.Element | null {
  return (data: WarningQuery): JSX.Element | null => {
    if (!data.embeddedDatasources) {
      return null;
    }
    const dqwMap: { [key: string]: GraphDataQualityWarning } = {};

    // Go through our upstream data quality warnings and add them to a map by id to eliminate duplicates
    data.embeddedDatasources.forEach((eds) => {
      if (eds.upstreamDataQualityWarningsConnection) {
        eds.upstreamDataQualityWarningsConnection.nodes.forEach(dqw => {
          dqwMap[dqw.id] = dqw;
        });
      }
    });

    const upstreamWarnings = Object.keys(dqwMap).map(key => dqwMap[key]);

    const props: WarningStatusProps = {
      subjectArea: 'View',
      kind: 'graph',
      side: 'left',
      anchoredAsset: null,
      anchoredWarnings: [],
      iconOnly: false,
      urls,
      upstreamWarnings
    };

    return <WarningStatusChip {...props} />
  }
}
